import { useNavigate, useSearch, type LinkProps } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'
import type { z } from 'zod'

/**
 * Addresses for the three collection screens.
 *
 * TanStack Router types `to` against the registered route tree, and these routes are registered by
 * the integrator after this area is built — so until that happens the checker cannot know they
 * exist. Rather than scatter casts through a dozen components, the widening happens here, once,
 * behind a union that names exactly the three paths this area owns. When the routes are wired the
 * only thing that changes is that the cast stops being necessary; nothing about the call sites
 * does.
 *
 * The paths are also what `collectionsRoutes` in `src/routes/collections/index.ts` reports, so
 * there is one place a path is written down and one place it can be got wrong.
 */
export const COLLECTION_PATHS = {
  worklist: '/collections',
  groupMeetings: '/collections/$groupId',
  sheet: '/collections/$groupId/$meetingId',
} as const

/** Where a link or a redirect inside this area can go, with the parameters that path needs. */
export type CollectionDestination =
  | { screen: 'worklist' }
  | { screen: 'groupMeetings'; groupId: string }
  | { screen: 'sheet'; groupId: string; meetingId: string }

export interface ResolvedDestination {
  to: LinkProps['to']
  params: Record<string, string>
}

export function resolveDestination(destination: CollectionDestination): ResolvedDestination {
  switch (destination.screen) {
    case 'groupMeetings':
      return {
        to: COLLECTION_PATHS.groupMeetings,
        params: { groupId: destination.groupId },
      }
    case 'sheet':
      return {
        to: COLLECTION_PATHS.sheet,
        params: { groupId: destination.groupId, meetingId: destination.meetingId },
      }
    default:
      return { to: COLLECTION_PATHS.worklist, params: {} }
  }
}

/**
 * Navigating between the collection screens.
 *
 * A `replace` is offered because moving from a sheet back to the group's meetings after a batch
 * has posted is not a place someone means to return to with the Back button — the sheet they came
 * from has already been posted and re-opening it would show an empty grid over settled balances.
 */
export const useCollectionNavigate = () => {
  const navigate = useNavigate()

  // Memoised, because the worklist puts this function's sibling in the dependency array of the
  // search debounce. A new identity on every render would clear and restart that timer on every
  // render — including a background refetch — so a debounce that is meant to fire 300ms after the
  // last keystroke would instead never fire on a screen that is quietly refetching.
  return useCallback(
    (destination: CollectionDestination, replace = false) => {
      const { to, params } = resolveDestination(destination)
      void navigate({ to, params: params as never, replace })
    },
    [navigate],
  )
}

/**
 * This area's own search parameters, read back through a schema it owns.
 *
 * The same registration gap that widens `to` also means the router does not yet know this area's
 * search schema, so `useSearch` cannot type it. Reading the raw object and validating it here
 * gives the same guarantees the other areas get from `validateSearch` — every value is read back
 * through `.catch()`, so a hand-edited or stale URL degrades to the default rather than throwing
 * an operator out of the screen — and when the integrator wires `validateSearch` with the very
 * schema exported from `src/routes/collections/search.ts`, nothing about this changes except that
 * the validation happens one layer earlier.
 */
export function useCollectionSearch<TSchema extends z.ZodType<object>>(
  schema: TSchema,
): z.infer<TSchema> {
  const raw: unknown = useSearch({ strict: false })

  return useMemo(() => {
    const parsed = schema.safeParse(raw)
    // An empty object is always valid: every member of these schemas is optional, so this is the
    // "no filters" state rather than a fallback that hides a bug.
    return parsed.success ? parsed.data : schema.parse({})
  }, [schema, raw])
}

/** Moves within this area while replacing the search parameters wholesale. */
export const useCollectionSearchNavigate = () => {
  const navigate = useNavigate()

  // Memoised for the reason given on {@link useCollectionNavigate}: this one is the function the
  // worklist's search debounce actually depends on.
  return useCallback(
    (destination: CollectionDestination, search: object, replace = false) => {
      const { to, params } = resolveDestination(destination)
      void navigate({ to, params: params as never, search: search as never, replace })
    },
    [navigate],
  )
}
